/**
 * 权限操作相关Hooks
 * 包含权限查看、树形数据处理等功能
 */
import { useState, useEffect, useMemo } from "react";
import type { TreeNode } from "@ms/shared-components/components/GenericModalTree";
import { Role } from "../config/panelTableConfig";
import { RolePermissionService } from "../services/rolePermissionService";

// ==================== 类型定义 ====================

export interface UseRolePermissionTreeDataProps {
  permissions: any[];
  currentRecord: Role | null;
}

export interface UsePermissionViewModalReturn {
  // 权限查看模态框状态
  permissionViewVisible: boolean;
  currentRecord: Role | null;
  permissions: any[];
  permissionLoading: boolean;

  // 操作方法
  showPermissionModal: (role: Role) => void;
  hidePermissionModal: () => void;
  setCurrentRecord: (role: Role | null) => void;
  fetchPermissions: () => Promise<void>;
}

// ==================== 权限树形数据Hook ====================

/**
 * 角色权限树形数据Hook
 * 将权限数据转换为树形结构用于显示
 */
export const useRolePermissionTreeData = ({ permissions, currentRecord }: UseRolePermissionTreeDataProps) => {

  // 将权限数据转换为树形结构
  const permissionTreeData = useMemo((): TreeNode[] => {
    if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
      return [];
    }

    const result: TreeNode[] = [];
    const roleCode = currentRecord?.code || "";
    const roleName = currentRecord?.name || "";

    // 创建角色根节点
    // 第一层：显示 "code 中文名 (共X个权限)"
    const roleDisplayName = roleCode && roleName ? `${roleCode} ${roleName}` : roleName || roleCode;
    const roleNode: TreeNode = {
      key: `role-${currentRecord?.id}`,
      title: `${roleDisplayName} (共 ${permissions.length} 个权限)`,
      children: [],
    };

    // 按服务分组权限
    const groupedByService = (permissions as any[]).reduce(
      (acc, permission) => {
        // 从权限代码中解析服务名（格式：service:module:action），确保使用英文服务名
        const codeParts = permission.code?.split(":") || [];
        const serviceName = codeParts.length >= 1 ? codeParts[0] : (permission.serviceName || permission.service_name || "unknown");
        if (!acc[serviceName]) {
          acc[serviceName] = [];
        }
        acc[serviceName].push(permission);
        return acc;
      },
      {} as Record<string, any[]>
    );

    // 为每个服务创建节点
    Object.entries(groupedByService).forEach(([serviceName, servicePermissions]) => {
      // 第二层：只显示英文服务名 (数量)
      const serviceNode: TreeNode = {
        key: `service-${serviceName}`,
        title: `${serviceName} (${(servicePermissions as any[]).length} 个权限)`,
        children: [],
      };

      // 按模块分组权限
      const groupedByModule = (servicePermissions as any[]).reduce(
        (acc, permission) => {
          // 解析权限代码，提取模块名
          const codeParts = permission.code?.split(":") || [];
          let module: string;

          if (permission.code === "*") {
            // 全局通配符权限
            module = "global";
          } else if (codeParts.length >= 2) {
            // 标准格式：service:module:action
            module = codeParts[1];
          } else {
            // 其他格式，使用服务名作为模块
            module = serviceName;
          }

          if (!acc[module]) {
            acc[module] = [];
          }
          acc[module].push(permission);
          return acc;
        },
        {} as Record<string, any[]>
      );

      // 为每个模块创建页面节点
      Object.entries(groupedByModule).forEach(([module, modulePermissions]) => {
        // 第三层：只显示英文，格式为 "service:module (数量)"
        const moduleNode: TreeNode = {
          key: `module-${serviceName}-${module}`,
          title: `${serviceName}:${module} (${(modulePermissions as any[]).length} 个操作)`,
          children: [],
        };

        // 为每个权限创建操作节点
        (modulePermissions as any[]).forEach((permission) => {
          // 第四层：只显示英文权限代码
          const permissionNode: TreeNode = {
            key: permission.id || permission.code,
            title: permission.code || "unknown",
            isLeaf: true,
          };

          moduleNode.children?.push(permissionNode);
        });

        serviceNode.children?.push(moduleNode);
      });

      roleNode.children?.push(serviceNode);
    });

    result.push(roleNode);
    return result;
  }, [permissions, currentRecord?.id, currentRecord?.name, currentRecord?.code]);

  return {
    permissionTreeData,
  };
};

// ==================== 权限查看弹窗Hook ====================

/**
 * 权限查看弹窗Hook
 * 管理权限查看弹窗的状态和数据获取
 */
export const usePermissionViewModal = (): UsePermissionViewModalReturn => {
  const [permissionViewVisible, setPermissionViewVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [permissionLoading, setPermissionLoading] = useState(false);

  // 获取权限数据
  const fetchPermissions = async () => {
    if (!currentRecord?.id || !permissionViewVisible) return;

    setPermissionLoading(true);
    try {
      const response = await RolePermissionService.getRolePermissions(currentRecord.id);
      if (response.success) {
        // 后端返回的数据格式：{ success: true, data: permissions }
        // 其中 data 直接是权限数组，不是 { permissions: [...] }
        const permissionsData = response.data;
        if (Array.isArray(permissionsData)) {
          setPermissions(permissionsData);
        } else if (permissionsData && Array.isArray(permissionsData.permissions)) {
          // 兼容旧格式：{ permissions: [...] }
          setPermissions(permissionsData.permissions);
        } else {
          console.warn("权限数据格式不正确:", permissionsData);
          setPermissions([]);
        }
      } else {
        console.error("获取角色权限失败:", response.message);
        setPermissions([]);
      }
    } catch (error) {
      console.error("获取角色权限失败:", error);
      setPermissions([]);
    } finally {
      setPermissionLoading(false);
    }
  };

  // 显示权限查看模态框
  const showPermissionModal = (role: Role) => {
    setCurrentRecord(role);
    setPermissionViewVisible(true);
  };

  // 隐藏权限查看模态框
  const hidePermissionModal = () => {
    setPermissionViewVisible(false);
    setCurrentRecord(null);
    setPermissions([]);
  };

  // 监听权限查看弹窗打开，获取权限数据
  useEffect(() => {
    if (permissionViewVisible && currentRecord?.id) {
      fetchPermissions();
    }
  }, [permissionViewVisible, currentRecord?.id]);

  return {
    // 权限查看模态框状态
    permissionViewVisible,
    currentRecord,
    permissions,
    permissionLoading,

    // 操作方法
    showPermissionModal,
    hidePermissionModal,
    setCurrentRecord,
    fetchPermissions,
  };
};
